/**
 * M15: Visual campaign canvas — renders campaign page like BlogPostCanvas.
 * Blocks shown via BlockRenderer with dnd-kit reordering; campaign metadata
 * (title, slug, goal, dates, published) is edited in CampaignsTab's inline editor.
 */
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { Lock } from "lucide-react"
import { SortableBlock, CanvasSiteFooter } from "./BlogPostCanvas"
import { PublicNavBar } from "@/components/PublicNavBar"
import { parseAnnouncement } from "@/lib/announcement"
import { useGoogleFont } from "@/hooks/useGoogleFont"
import type { OrgCustomizations, Organization, PageSection } from "@/templates/types"
import type { NavItem } from "@/types/navigation"

export interface CampaignLiveData {
  id: string
  title: string
  slug: string
  goal_cents: number
  start_date: string | null
  end_date: string | null
  is_published: boolean
  blocks: PageSection[]
}

export interface CampaignCanvasProps {
  liveData: CampaignLiveData
  customizations: OrgCustomizations | null
  org: Organization | null
  navItems?: NavItem[]
  pages?: Array<{ id: string; slug: string; title: string | null }>
  selectedSectionId: string | null
  onSectionClick: (sectionId: string, e: React.MouseEvent) => void
  onReorderBlocks?: (blocks: PageSection[]) => void
  footerSection?: PageSection | null
  isMobileViewport?: boolean
  primaryColor: string
}

function CampaignProgressPlaceholder({ primaryColor }: { primaryColor: string }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        padding: "48px 40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 4,
          color: "#64748b",
        }}
      >
        <Lock size={14} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            letterSpacing: "0.06em",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Campaign Progress
        </span>
      </div>
      <div style={{ width: "100%", maxWidth: 480, background: "#e2e8f0", borderRadius: 6, height: 10 }}>
        <div
          style={{
            background: primaryColor,
            width: "38%",
            height: "100%",
            borderRadius: 6,
            opacity: 0.6,
          }}
        />
      </div>
      <div
        style={{
          marginTop: 12,
          padding: "10px 28px",
          background: primaryColor,
          borderRadius: 8,
          color: "#fff",
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          opacity: 0.6,
        }}
      >
        Donate
      </div>
      <p
        style={{
          fontSize: 11,
          color: "#94a3b8",
          fontFamily: "'DM Sans', sans-serif",
          margin: 0,
          textAlign: "center" as const,
          maxWidth: 340,
        }}
      >
        Progress bar, donate CTA, and ended state appear here automatically on the public page.
      </p>
    </div>
  )
}

export function CampaignCanvas({
  liveData,
  customizations,
  org,
  navItems = [],
  pages = [],
  selectedSectionId,
  onSectionClick,
  onReorderBlocks,
  footerSection,
  isMobileViewport = false,
  primaryColor,
}: CampaignCanvasProps) {
  const fontFamily = customizations?.font_family ?? "Inter, sans-serif"
  const secondaryColor = customizations?.secondary_color ?? "#1e40af"

  useGoogleFont(fontFamily)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const cssVars = {
    "--primary-color": primaryColor,
    "--secondary-color": secondaryColor,
    "--font-family": fontFamily,
  } as React.CSSProperties

  const sortedBlocks = [...liveData.blocks].sort(
    (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !onReorderBlocks) return
    const fromIdx = sortedBlocks.findIndex((b) => b.id === active.id)
    const toIdx = sortedBlocks.findIndex((b) => b.id === over.id)
    if (fromIdx === -1 || toIdx === -1) return
    onReorderBlocks(arrayMove(sortedBlocks, fromIdx, toIdx))
  }

  if (!customizations) {
    return (
      <div
        style={{
          padding: 48,
          color: "#999",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    )
  }

  const isFooterSelected = footerSection ? selectedSectionId === footerSection.id : false

  return (
    // NOS-156: containerType makes this a size container for @sm/@md/@lg variants.
    // NOS-151: section wrappers paint nothing — the root supplies the page background.
    <div style={{ all: "initial", containerType: "inline-size", fontFamily, display: "block", background: "#ffffff", ...cssVars }}>
      <PublicNavBar
        orgName={org?.name ?? "Organization"}
        logoUrl={org?.logo_url}
        navItems={navItems}
        pages={pages}
        primaryColor={primaryColor}
        navCtaText={customizations?.nav_cta_text ?? "Donate"}
        announcement={parseAnnouncement(customizations?.announcement)}
        fontFamily={fontFamily}
        isMobileViewport={isMobileViewport}
        selectable
        selectedSectionId={selectedSectionId}
        onSectionClick={onSectionClick}
        logoScale={(customizations?.logo_scale as number | undefined) ?? 1.0}
      />

      {sortedBlocks.length === 0 ? (
        <div
          style={{
            background: "#fff",
            padding: "48px 40px",
            textAlign: "center" as const,
            color: "#94a3b8",
            fontSize: 14,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          No content sections yet. Use the panel on the left to add sections.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedBlocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedBlocks.map((block) => (
              <SortableBlock
                key={block.id}
                section={block}
                primaryColor={primaryColor}
                customizations={customizations}
                org={org}
                selectedSectionId={selectedSectionId}
                onSectionClick={onSectionClick}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <CampaignProgressPlaceholder primaryColor={primaryColor} />

      {footerSection && (
        <CanvasSiteFooter
          footerSection={footerSection}
          primaryColor={primaryColor}
          customizations={customizations}
          org={org}
          isSelected={isFooterSelected}
          onSectionClick={onSectionClick}
        />
      )}
    </div>
  )
}
